import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { Card } from '@ipfabric/ui-components/Card/Card';
import { FileUpload } from '@ipfabric/ui-components/FileUpload/FileUpload';
import { FormCheckbox } from '@ipfabric/ui-components/FormCheckbox/FormCheckbox';
import { FormTextArea } from '@ipfabric/ui-components/FormTextArea/FormTextArea';
import { FormTextField } from '@ipfabric/ui-components/FormTextField/FormTextField';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { SidePanel } from '@ipfabric/ui-components/SidePanel/SidePanel';
import type { NonEmptyString } from '@ipfabric/utils';
import { bgpProtocols } from 'components/BgpSettings/dataTransfer/bgpDataTransferTypes';
import type { FC } from 'react';
import React, { useEffect, useState } from 'react';
import { type SubmitHandler, useWatch } from 'react-hook-form';
import { generateGuid } from 'utils/guid';

import { FormSelect } from '@ipfabric/ui-components/FormSelect/FormSelect';
import { useCustomForm } from '@ipfabric/ui-components/utils/hookForm/useCustomForm';
import {
  type LoadingPhase,
  withLoadingIndicator,
} from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type { BgpCommunities } from '@ipfabric/utils/settings/types/types';
import {
  type BgpFieldValues,
  type BgpOutputValues,
  bgpFieldValues,
} from 'components/BgpSettings/BgpFieldValues';
import { parseAndValidateDataFromCSV } from './helpers/parseAndValidateDataFromCSV';
import { processAndValidateCSV } from './helpers/processAndValidateCSV';

interface Props {
  editedItem: BgpCommunities | null;
  formType: 'add' | 'edit';
  onSubmit: (data: BgpOutputValues) => void;
  onClose: () => void;
}

export const BgpDeviceForm: FC<Props> = (props) => {
  const [bgpFile, setBgpFile] = React.useState<File | null>(null);
  const fileUploadRef = React.useRef<HTMLInputElement>(null);
  const [loadingState, setLoadingState] = useState<LoadingPhase>('LOADING');

  const {
    clearErrors,
    control,
    formState,
    handleSubmit,
    resetField,
    setError,
    setValue,
  } = useCustomForm<BgpFieldValues, unknown, BgpOutputValues>({
    defaultValues: props.editedItem
      ? {
          ...props.editedItem,
          vrf: props.editedItem.vrf ?? '',
          communityList: props.editedItem.communityList.join(','),
        }
      : {
          // Temporarily generated 'id' is used on the UI, as it is necessary for operations such as deleting an entry in the table. The backend ignores it and assigns its own 'id' afterward.
          id: generateGuid() as NonEmptyString,
          sn: '',
          protocol: null,
          vrf: '',
          isVrfStd: false,
          communityList: '',
        },
    resolver: zodResolver(bgpFieldValues),
  });

  // Watch the values of `vrf` and `isVrfStd`
  const vrf = useWatch({ control, name: 'vrf' });
  const isVrfStd = useWatch({ control, name: 'isVrfStd' });

  // Clear `vrf` if `isVrfStd` is checked
  useEffect(() => {
    if (isVrfStd && vrf) {
      setValue('vrf', '');
    }
  }, [isVrfStd, vrf, setValue]);

  useEffect(() => {
    if (formState.dirtyFields.vrf && vrf) {
      clearErrors('isVrfStd');
    }
  }, [formState, vrf, clearErrors]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: invoke CSV processing only when bgpFile exists
  useEffect(() => {
    (async () => {
      await withLoadingIndicator(setLoadingState)(async () => {
        if (bgpFile) {
          const result = await processAndValidateCSV(bgpFile);
          if (result.error) {
            setError('communityList', {
              message: result.error,
            });
          }
          if (result.content) {
            const list: string[] = [];
            parseAndValidateDataFromCSV(
              { content: result.content },
              ({ line }) => {
                const value = line.split(',').map((v) => v.trim())[0];
                value && list.push(value);
              },
            ).then(() => {
              if (list.length) {
                setValue('communityList', list.join(','), {
                  shouldDirty: true,
                  shouldValidate: true,
                  shouldTouch: true,
                });
              }
            });
          }
        }
      });
    })();
  }, [bgpFile]);

  const onSubmit: SubmitHandler<BgpOutputValues> = (values) => {
    props.onSubmit(values);
    props.onClose();
  };

  return (
    <SidePanel
      title={props.formType === 'add' ? 'Add device' : 'Edit device'}
      closeOnBackdropClick={false}
      onClose={props.onClose}
      footer={({ closeModal }) => (
        <div className='flex gap-xs'>
          <Button
            className='flex-1'
            variant='primary'
            type='submit'
            icon='fa-floppy-disk'
            disabled={!formState.isDirty}
            loading={formState.isSubmitting}
            onClick={handleSubmit(onSubmit)}
            dataTestId='routing-add-device'
          >
            {props.formType === 'add' ? 'Add device' : 'Save changes'}
          </Button>
          <Button className='flex-1' variant='outline' onClick={closeModal}>
            Close
          </Button>
        </div>
      )}
    >
      <form>
        <Card className='flex flex-col gap p-sm'>
          <FormTextField
            control={control}
            name='sn'
            type='text'
            label='Serial number'
            onClear={
              formState.dirtyFields.sn
                ? () => {
                    resetField('sn');
                  }
                : undefined
            }
          />
          <FormSelect.Optional
            control={control}
            name='protocol'
            label='Protocol'
            defaultValue='IPv6'
            options={bgpProtocols}
            placeholder='Choose protocol'
          />
          <div className='flex flex-col gap-xs'>
            <FormTextField
              control={control}
              name='vrf'
              type='text'
              label='VRF name'
              disabled={isVrfStd}
              rules={{ required: !isVrfStd }}
              onClear={
                formState.dirtyFields.vrf
                  ? () => {
                      resetField('vrf');
                    }
                  : undefined
              }
            />
            <FormCheckbox
              control={control}
              name='isVrfStd'
              description='Use native VRF name'
              className='font-normal'
            />
          </div>
          <div className='flex flex-col gap-3xs'>
            <FormTextArea
              control={control}
              name='communityList'
              label='List of communities'
              labelTooltip={
                'Use a comma separator when inserting multiple values. You can also import multiple values by uploading a CSV file using the link below this field. The file should contain a single column of values without a header.'
              }
            />
            <Link
              renderAs='button'
              onClick={() => {
                fileUploadRef.current?.click();
              }}
              icon='fa-file-import'
              iconPosition='start'
              type='button'
              className='self-start'
              loading={loadingState === 'LOADING'}
            >
              Import from file
            </Link>
            <FileUpload
              value={bgpFile}
              onFileSelected={(e) => {
                setBgpFile(e);
              }}
              externalRef={fileUploadRef}
              className='hidden'
            />
          </div>
        </Card>
      </form>
    </SidePanel>
  );
};
